import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import UploadZone from './components/UploadZone';
import ImagePreviewGrid from './components/ImagePreviewGrid';
import BikeTypeSelector from './components/BikeTypeSelector';
import UploadTipsPanel from './components/UploadTipsPanel';
import AdvancedOptions from './components/AdvancedOptions';
import UploadProgressIndicator from '../../components/ui/UploadProgressIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const PhotoUpload = () => {
  const navigate = useNavigate();
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedBikeType, setSelectedBikeType] = useState('');
  const [advancedOptions, setAdvancedOptions] = useState({
    focusAreas: [],
    analysisDepth: 'standard',
    budgetRange: 'moderate',
    priorityType: 'performance'
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('uploading');
  const [showProgress, setShowProgress] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check authentication status
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    setIsAuthenticated(true);
  }, [navigate]);

  const createImagePreview = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve({
          ...file,
          preview: e.target.result
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFilesSelected = async (files) => {
    const imagePromises = files?.map(createImagePreview);
    const imagesWithPreviews = await Promise.all(imagePromises);
    setSelectedImages(prev => [...prev, ...imagesWithPreviews]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev?.filter((_, i) => i !== index));
  };

  const handleReorderImages = (newImages) => {
    setSelectedImages(newImages);
  };

  const handleBikeTypeChange = (type) => {
    setSelectedBikeType(type);
  };

  const handleAdvancedOptionsChange = (options) => {
    setAdvancedOptions(options);
  };

  const simulateUploadProgress = () => {
    const stages = ['uploading', 'processing', 'analyzing', 'generating', 'complete'];
    let currentStageIndex = 0;
    let progress = 0;

    const interval = setInterval(() => {
      progress += Math.random() * 15 + 5; // Random progress between 5-20%
      
      if (progress >= 100) {
        progress = 100;
        setUploadProgress(progress);
        setUploadStage('complete');
        clearInterval(interval);
        
        // Redirect to results after a short delay
        setTimeout(() => {
          navigate('/bike-analysis-results');
        }, 2000);
        return;
      }

      // Change stage based on progress
      const stageProgress = progress / 100 * stages?.length;
      const newStageIndex = Math.min(Math.floor(stageProgress), stages?.length - 2);
      
      if (newStageIndex > currentStageIndex) {
        currentStageIndex = newStageIndex;
        setUploadStage(stages?.[currentStageIndex]);
      }

      setUploadProgress(progress);
    }, 800);

    return interval;
  };

  const handleStartAnalysis = async () => {
    if (selectedImages?.length === 0) {
      alert('Please select at least one image to analyze.');
      return;
    }

    if (!selectedBikeType) {
      alert('Please select your bike type for better analysis.');
      return;
    }

    setIsUploading(true);
    setShowProgress(true);
    setUploadProgress(0);
    setUploadStage('uploading');

    // Save analysis data to localStorage for results page
    const analysisData = {
      images: selectedImages?.map(img => ({
        name: img?.name,
        size: img?.size,
        preview: img?.preview
      })),
      bikeType: selectedBikeType,
      options: advancedOptions,
      timestamp: new Date()?.toISOString()
    };
    
    localStorage.setItem('pendingAnalysis', JSON.stringify(analysisData));

    // Start upload simulation
    simulateUploadProgress();
  };

  const handleCancelUpload = () => {
    setIsUploading(false);
    setShowProgress(false);
    setUploadProgress(0);
    setUploadStage('uploading');
  };

  const canStartAnalysis = selectedImages?.length > 0 && selectedBikeType && !isUploading;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={32} className="animate-spin text-accent mx-auto mb-4" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-accent/10 rounded-lg">
                <Icon name="Camera" size={24} color="var(--color-accent)" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Upload Your Bike Photos</h1>
                <p className="text-muted-foreground">
                  Get AI-powered upgrade recommendations by uploading clear photos of your bike
                </p>
              </div>
            </div>
            
            {/* Progress Steps */}
            <div className="flex items-center space-x-4 text-sm">
              <div className={`flex items-center space-x-2 ${
                selectedImages?.length > 0 ? 'text-accent' : 'text-muted-foreground'
              }`}>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedImages?.length > 0 ? 'border-accent bg-accent text-accent-foreground' : 'border-muted-foreground'
                }`}>
                  {selectedImages?.length > 0 ? <Icon name="Check" size={14} /> : '1'}
                </div>
                <span>Upload Photos</span>
              </div>
              
              <div className={`w-8 h-0.5 ${
                selectedBikeType ? 'bg-accent' : 'bg-muted-foreground/30'
              }`} />
              
              <div className={`flex items-center space-x-2 ${
                selectedBikeType ? 'text-accent' : 'text-muted-foreground'
              }`}>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedBikeType ? 'border-accent bg-accent text-accent-foreground' : 'border-muted-foreground'
                }`}>
                  {selectedBikeType ? <Icon name="Check" size={14} /> : '2'}
                </div>
                <span>Select Bike Type</span>
              </div>
              
              <div className={`w-8 h-0.5 ${
                canStartAnalysis ? 'bg-accent' : 'bg-muted-foreground/30'
              }`} />
              
              <div className={`flex items-center space-x-2 ${
                canStartAnalysis ? 'text-accent' : 'text-muted-foreground'
              }`}>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  canStartAnalysis ? 'border-accent' : 'border-muted-foreground'
                }`}>
                  3
                </div>
                <span>Start Analysis</span>
              </div>
            </div>
          </div>

          {/* Upload Progress Modal */}
          {showProgress && (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <UploadProgressIndicator
                progress={uploadProgress}
                stage={uploadStage}
                fileName={selectedImages?.[0]?.name}
                estimatedTime={uploadStage === 'complete' ? null : Math.max(30 - Math.floor(uploadProgress / 3), 5)}
                onCancel={uploadStage !== 'complete' ? handleCancelUpload : null}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Upload Zone */}
              <div className="bg-card border border-border rounded-lg p-6">
                <UploadZone
                  onFilesSelected={handleFilesSelected}
                  isUploading={isUploading}
                />
              </div>

              {/* Image Preview Grid */}
              {selectedImages?.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <ImagePreviewGrid
                    images={selectedImages}
                    onRemoveImage={handleRemoveImage}
                    onReorderImages={handleReorderImages}
                    isProcessing={isUploading}
                  />
                </div>
              )}

              {/* Bike Type Selector */}
              <div className="bg-card border border-border rounded-lg p-6">
                <BikeTypeSelector
                  selectedType={selectedBikeType}
                  onTypeChange={handleBikeTypeChange}
                  disabled={isUploading}
                />
              </div>

              {/* Advanced Options */}
              <AdvancedOptions
                options={advancedOptions}
                onOptionsChange={handleAdvancedOptionsChange}
                disabled={isUploading}
              />

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Zap"
                  iconPosition="left"
                  onClick={handleStartAnalysis}
                  disabled={!canStartAnalysis}
                  loading={isUploading}
                  className="flex-1"
                >
                  {isUploading ? 'Analyzing Your Bike...' : 'Start AI Analysis'}
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={() => {
                    setSelectedImages([]);
                    setSelectedBikeType('');
                    setAdvancedOptions({
                      focusAreas: [],
                      analysisDepth: 'standard',
                      budgetRange: 'moderate',
                      priorityType: 'performance'
                    });
                  }}
                  disabled={isUploading}
                >
                  Reset
                </Button>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Upload Tips */}
              <UploadTipsPanel />

              {/* Quick Stats */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Analysis Overview
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Photos Selected</span>
                    <span className="text-sm font-medium text-foreground">
                      {selectedImages?.length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Bike Type</span>
                    <span className="text-sm font-medium text-foreground">
                      {selectedBikeType || 'Not selected'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Analysis Depth</span>
                    <span className="text-sm font-medium text-foreground capitalize">
                      {advancedOptions?.analysisDepth}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Budget Range</span>
                    <span className="text-sm font-medium text-foreground capitalize">
                      {advancedOptions?.budgetRange}
                    </span>
                  </div>
                </div>
              </div>

              {/* Help Section */}
              <div className="bg-accent/5 border border-accent/20 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <Icon name="HelpCircle" size={20} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-accent mb-2">Need Help?</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Our AI analyzes your bike photos to identify components and suggest upgrades based on your preferences and budget.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ExternalLink"
                      iconPosition="right"
                      onClick={() => window.open('/help/photo-upload', '_blank')}
                    >
                      View Guide
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PhotoUpload;